import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AccountType } from '@tabita/contracts';
import { ACCOUNT_TYPES } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

export function CreateAccountDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('ASSET');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () => financeApi.createAccount({ code, name, type }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="create-account-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="create-account-title">{t('finance.account.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('finance.account.code')}
          name="code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          dir="ltr"
          required
          autoFocus
        />
        <Field
          label={t('finance.account.name')}
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />

        <div className="field">
          <label className="field-label" htmlFor="account-type">
            {t('finance.account.type')}
          </label>
          <select
            id="account-type"
            className="field-input"
            value={type}
            onChange={(event) => setType(event.target.value as AccountType)}
          >
            {ACCOUNT_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`finance.accountType.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!code.trim() || !name.trim()}
            loading={mutation.isPending}
          >
            {t('finance.account.create')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
